/**
 * InmateEscapeModal.tsx — R4 jail UX (CENTERPIECE — dramatic escape)
 *
 * Opens when player E-presses / clicks the Inmate NPC on the Jail tile.
 * Replaces silent attemptEscapeJail() call.
 *
 * On "ATTEMPT ESCAPE":
 *   - calls attemptEscapeJail() (store sets jailEscapeResult + closes inmateEscapeModalOpen)
 *   - overlay flashes green (success) or red (fail)
 *   - result badge fades in for 3s, then auto-dismisses
 *
 * On "Not tonight" / Esc / backdrop: closes without attempting.
 *
 * VISIBLE WHEN: inmateEscapeModalOpen === true
 * TEST PATH: land on Jail tile → walk to inmate NPC (right edge) → E / click → modal opens
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./InmateEscapeModal.module.css";

// ---------------------------------------------------------------------------
// Narrative variants — inmate whisper lines
// ---------------------------------------------------------------------------
const INMATE_LINES = [
  "There's a loose brick. Tonight, the guard naps at 3am. Are you in?",
  "We've got 5 seconds before the searchlight swings back. Decide now.",
  "I've timed the rotation. One chance. You with me?",
  "The east wall's weak. I've been digging for weeks. Tonight's the night.",
  "Guard changes in 90 seconds. Move now or wait another round.",
  "Heard the shift-change bell? That's our window. Don't blow it.",
  "Trust me. I've done this before. Mostly.",
];

function pickInmateLine(seed: number): string {
  return (
    INMATE_LINES[seed % INMATE_LINES.length] ??
    "There's a loose brick. Tonight, the guard naps at 3am. Are you in?"
  );
}

const RESULT_SHOW_MS = 3000;
// R15-AS: shake duration before revealing the d100 result (boardAnimationActive gate
// is not used here — InmateEscapeModal is a modal flow, not a board action).
const SHAKE_DURATION_MS = 600;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InmateEscapeModal(): React.ReactElement | null {
  const { t } = useI18n();
  const open = useGameStore((s) => s.inmateEscapeModalOpen);
  const closeInmateEscapeModal = useGameStore((s) => s.closeInmateEscapeModal);
  const attemptEscapeJail = useGameStore((s) => s.attemptEscapeJail);

  const jailEscapeResult = useGameStore((s) => s.jailEscapeResult);
  const dismissJailEscapeResult = useGameStore((s) => s.dismissJailEscapeResult);

  const currentSeat = useGameStore((s) => s.currentSeat);
  const tokens = useGameStore((s) => s.tokens);
  const bailMultiplier = useGameStore((s) => s.bailMultiplier);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const jailEscapeAttempts = useGameStore((s) => s.jailEscapeAttempts);
  const jailEscapeUsedThisTurn = useGameStore((s) => s.jailEscapeUsedThisTurn);
  const jailTurnsRemaining = useGameStore((s) => s.jailTurnsRemaining);
  const markNpcInteracted = useGameStore((s) => s.markNpcInteracted);
  const playerTiles = useGameStore((s) => s.playerTiles);

  // Stable narrative variant chosen once per open
  const lineRef = useRef<string>("");
  const prevOpenRef = useRef(false);
  if (open && !prevOpenRef.current) {
    lineRef.current = pickInmateLine(currentSeat + (Date.now() % 113));
  }
  prevOpenRef.current = open;

  // Focus the panel on open
  const panelRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  // R15-AS: tin-cup rattle shake phase. True while shake animation runs (600ms).
  // attemptEscapeJail() is deferred until shake completes so players see the
  // rattle before the d100 result appears.
  const [isShaking, setIsShaking] = useState(false);
  const shakeTimerRef = useRef<number | null>(null);

  // Esc closes the modal (not-tonight path) — only when no result is showing
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && jailEscapeResult === null) {
        e.preventDefault();
        closeInmateEscapeModal();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, jailEscapeResult, closeInmateEscapeModal]);

  // Auto-dismiss result badge after RESULT_SHOW_MS
  useEffect(() => {
    if (jailEscapeResult === null) return;
    const t = window.setTimeout(() => {
      dismissJailEscapeResult();
    }, RESULT_SHOW_MS);
    return () => window.clearTimeout(t);
  }, [jailEscapeResult, dismissJailEscapeResult]);

  // R15-AS: cleanup shake timer on unmount.
  useEffect(() => {
    return () => {
      if (shakeTimerRef.current !== null) window.clearTimeout(shakeTimerRef.current);
    };
  }, []);

  // Show result toast even after modal closed (store drives it)
  const showResult = jailEscapeResult !== null;

  if (!open && !showResult) return null;

  const attempts = jailEscapeAttempts[currentSeat] ?? 0;
  const usedThisTurn = jailEscapeUsedThisTurn[currentSeat] ?? false;
  const turnsLeft = jailTurnsRemaining[currentSeat] ?? 0;

  // Compute bail price (mirror store computeBailPrice): mean over PRESENT seats
  // only — absent seats >= numPlayers keep INITIAL_TOKENS=100 and would inflate it.
  const presentCount = numPlayers ?? 4;
  const mean =
    tokens.slice(0, presentCount).reduce<number>((sum, t) => sum + (t ?? 0), 0) / presentCount;
  const mult = bailMultiplier[currentSeat] ?? 0.25;
  const bailPrice = Math.max(1, Math.round(mean * mult));

  const canAttempt = attempts < 2 && !usedThisTurn;

  // Compute what happens on fail (for warning text)
  const nextMult = mult * 2;
  const failBailNext = Math.max(1, Math.round(mean * nextMult));

  function handleAttempt(): void {
    if (isShaking) return; // prevent double-fire during shake
    // R15-AS: play tin-cup rattle for SHAKE_DURATION_MS before firing the store action.
    // This lets the animation complete before the result toast replaces the modal.
    setIsShaking(true);
    shakeTimerRef.current = window.setTimeout(() => {
      setIsShaking(false);
      shakeTimerRef.current = null;
      attemptEscapeJail();
      // Mark NPC interacted (speech bubble switches to completed line)
      const tileIdx = playerTiles[currentSeat] ?? 0;
      markNpcInteracted(tileIdx, "inmate.png");
      // Modal and result handled by store: inmateEscapeModalOpen→false, jailEscapeResult→{success}
    }, SHAKE_DURATION_MS);
  }

  function handleNot(): void {
    // R15 Codex-AS CRITICAL fix: cancel any in-flight shake timer so the
    // 600ms-deferred attemptEscapeJail() doesn't fire after the player cancels.
    if (shakeTimerRef.current !== null) {
      window.clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = null;
    }
    setIsShaking(false);
    closeInmateEscapeModal();
  }

  // Overlay flash class driven by result
  const overlayFlashClass =
    jailEscapeResult !== null
      ? jailEscapeResult.success
        ? ` ${styles.overlayFlashSuccess}`
        : ` ${styles.overlayFlashFail}`
      : "";

  return (
    <>
      {/* Result toast — shown above everything for 3s */}
      {showResult && (
        <div className={styles.resultToast} data-testid="escape-result-toast">
          <div
            className={`${styles.resultBadge} ${jailEscapeResult?.success ? styles.resultSuccess : styles.resultFail}`}
            data-testid={jailEscapeResult?.success ? "escape-success-badge" : "escape-fail-badge"}
          >
            {jailEscapeResult !== null && (
              <span className={styles.resultRoll} data-testid="escape-roll-display">
                {`Rolled ${jailEscapeResult.roll}/${jailEscapeResult.threshold} — `}
              </span>
            )}
            {jailEscapeResult?.success
              ? "FREE! You escaped."
              : "CAUGHT! Bail raised, sentence extended."}
          </div>
        </div>
      )}

      {/* Modal — only when still open (store closes it on attempt) */}
      {open && (
        <div
          className={`${styles.overlay}${overlayFlashClass}`}
          data-testid="inmate-escape-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && jailEscapeResult === null) handleNot();
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Escape" || e.key === "Enter") &&
              e.target === e.currentTarget &&
              jailEscapeResult === null
            ) {
              handleNot();
            }
          }}
          role="presentation"
        >
          <dialog
            open
            className={styles.panel}
            aria-labelledby="inmate-modal-title"
            aria-describedby="inmate-modal-narrative"
            data-testid="inmate-escape-modal"
            ref={panelRef}
            tabIndex={-1}
            onCancel={(e) => {
              e.preventDefault();
              if (jailEscapeResult === null) handleNot();
            }}
          >
            <h2 id="inmate-modal-title" className={styles.title}>
              Cellmate Whispers...
            </h2>
            <p id="inmate-modal-narrative" className={styles.narrative}>
              "{lineRef.current}"
            </p>

            <div className={styles.statusBox}>
              <div className={styles.statusRow}>
                <span className={styles.statusLabel}>{t("inmateEscape.attemptsUsed")}</span>
                <span className={styles.statusValue}>{attempts}/2</span>
              </div>
              <div className={styles.statusRow}>
                <span className={styles.statusLabel}>{t("inmateEscape.currentBailPrice")}</span>
                <span className={styles.statusValue}>{bailPrice} tokens</span>
              </div>
              <div className={styles.statusRow}>
                <span className={styles.statusLabel}>{t("inmateEscape.turnsRemaining")}</span>
                <span className={styles.statusValue}>{turnsLeft}</span>
              </div>
            </div>

            <div className={styles.warning} data-testid="escape-warning">
              Failed escapes raise your bail (next: {failBailNext} tokens) and add 3 turns to your
              sentence. Max 2 attempts per jail visit.
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={[styles.attemptBtn, isShaking ? styles.shaking : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={handleAttempt}
                disabled={!canAttempt || isShaking}
                title={
                  isShaking
                    ? "Rolling…"
                    : attempts >= 2
                      ? "Max escape attempts used"
                      : usedThisTurn
                        ? "Already attempted this turn"
                        : "Attempt to escape"
                }
                data-testid="escape-attempt-btn"
                data-shaking={isShaking ? "true" : "false"}
              >
                {isShaking ? t("inmateEscape.rollingBtn") : t("inmateEscape.attemptBtn")}
              </button>
              <button
                type="button"
                className={styles.notBtn}
                onClick={handleNot}
                data-testid="escape-not-btn"
              >
                {t("inmateEscape.notTonightBtn")}
              </button>
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
